import { assignProperties } from '@attain-apps/object-utils'
import { indent } from '@attain-apps/string-utils'
import { TypeChecker } from '@attain-apps/type-guards'

// TODO: this could mostly be accomplished by just transforming an object to the
// desired structure and then using a library to stringify to YAML. The only
// reason we don't do that is so we have the option to do non-YAML formatting in
// certain places, if needed. However, if we encounter limitations with the
// below, it may be worth just using a YAML library rather than building this
// out very extensively

/**
 * Attempts to create a prompt that represents a given value
 *
 * If there is no useful prompt to represent the given value, this will return
 * `undefined`
 */
export type PromptGenerator<T> = PromptGenerator.Fn<T> & {
  readonly type: string
}
export namespace PromptGenerator {
  export type Fn<T> = (val: T) => string | undefined
  export function custom<T>(
    fn: Fn<T>,
    type: string | (() => string) = 'custom',
  ): PromptGenerator<T> {
    return assignProperties((...args: Parameters<typeof fn>) => fn(...args), {
      get type() {
        return typeof type === 'string' ? type : type()
      },
    })
  }

  export const STRING: PromptGenerator<string | undefined> = custom(
    val => globalThis.JSON.stringify(val),
    'string',
  )
  export const NUMBER: PromptGenerator<number | undefined> = custom(
    // NOTE: JSON.stringify is safer than toString since we may accidentally
    // receive a non-number
    val => globalThis.JSON.stringify(val),
    'number',
  )
  export const BOOLEAN: PromptGenerator<boolean | undefined> = custom(
    // NOTE: this is set up to ensure we end up with 'true', 'false', or
    // `undefined`, even if we receive a non-boolean input
    val => (TypeChecker.isNullish(val) ? undefined : val ? 'true' : 'false'),
    'boolean',
  )
  export const JSON: PromptGenerator<unknown> = custom(
    val => globalThis.JSON.stringify(val),
    'json',
  )

  /**
   * Represents a property of an object as a key-value pair
   */
  export type Property = [
    key: string,
    /**
     * __NOTE:__ we require values to already be stringified
     */
    val: string,
  ]

  export function property<T, P>(
    propExtractor: (obj: T) => P | undefined,
    propKeySrc: string | ((params: { object: T; property: P }) => string),
    propStringifier: PromptGenerator<P>,
  ): PromptGenerator<T> {
    return custom(obj => {
      const propVal = propExtractor(obj)
      let propValStr =
        propVal === undefined ? undefined : propStringifier(propVal)
      if (propVal === undefined || propValStr === undefined) {
        // TODO: evaluate if gen AI results are better when undefined properties
        // are omitted vs given a default value of something like 'None' or
        // 'Unknown'
        return undefined
      }
      const propKey =
        typeof propKeySrc === 'string'
          ? propKeySrc
          : propKeySrc({ object: obj, property: propVal })
      if (
        propStringifier.type === 'object' ||
        propStringifier.type === 'list' ||
        propValStr.includes('\n')
      ) {
        propValStr = indent(propValStr)
        if (!propValStr.startsWith('\n')) {
          propValStr = `\n${propValStr}`
        }
      }
      return `${propKey}:${/^\s/.test(propValStr) ? '' : ' '}${propValStr}`
    }, 'property')
  }

  export function object<T>(
    ...propStringifiers: readonly PromptGenerator<T>[]
  ): PromptGenerator<T> {
    return custom(
      obj =>
        propStringifiers
          .map(propStringifier => propStringifier(obj))
          // Only defined non-empty properties are kept
          .filter(prop => !!prop)
          .join('\n'),
      'object',
    )
  }

  export function list<T>(
    itemStringifier: PromptGenerator<T>,
  ): PromptGenerator<readonly T[] | undefined> {
    const itemPrefix = '- '
    return custom(val => {
      const stringifiedItems = val
        ?.map(item => {
          const str = itemStringifier(item)?.trim()
          // Empty strings and undefined are both removed
          return str
            ? `${itemPrefix}${indent(str, {
                // First line is already effectively indented above
                skipFirstLine: true,
                using: {
                  // Ensures each line is aligned with the first line
                  spaces: itemPrefix.length,
                },
              })}`
            : undefined
        })
        .filter(item => item !== undefined)
      return stringifiedItems?.length ? stringifiedItems.join('\n') : undefined
    }, 'list')
  }

  export function lazy<T>(
    generatorCreator: () => PromptGenerator<T>,
  ): PromptGenerator<T> {
    let generator: PromptGenerator<T> | undefined
    return custom(
      val => (generator ??= generatorCreator())(val),
      () => generator?.type ?? 'lazy',
    )
  }
}
